import { LitElement, html, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api } from '../api/client';
import type { EvalRun, EvalCaseResult, EvalCheck } from '../api/types';
import { renderSlidesDoc } from '../lib/render-slides';
import { setPageTitle } from '../lib/page-title';
import type { SlideDeck } from '../lib/slide-model';

/**
 * Eval decks gallery (/eval/decks). Renders EVERY generated deck of an eval run
 * with the V2 renderer (renderSlidesDoc → iframe srcdoc) so each case can be
 * visually evaluated against its expectations. One card per case carries the
 * checks, render-fit and judge alongside the live deck, plus Playwright data-
 * attributes for automated visual evaluation.
 *
 * On connect it loads the run summaries and picks the latest, or the run named
 * by `?run=<id>` when present. Light DOM so the global stylesheet applies; a
 * scoped <style> adds the gallery chrome.
 */
@customElement('eval-decks-page')
export class EvalDecksPage extends LitElement {
  @state() private run: EvalRun | null = null;
  @state() private error: string | null = null;
  @state() private loading = true;

  protected createRenderRoot(): HTMLElement {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    setPageTitle('Eval decks');
    void this.load();
  }

  private async load() {
    this.loading = true;
    this.error = null;
    try {
      const runs = await api.listEvalRuns();
      if (runs.length === 0) {
        this.run = null;
        this.loading = false;
        return;
      }
      const requested = new URLSearchParams(window.location.search).get('run');
      const pick = (requested && runs.find((r) => r.id === requested)) || runs[0];
      this.run = await api.getEvalRun(pick.id);
    } catch (err) {
      this.error = err instanceof Error ? err.message : 'Failed to load eval run';
    } finally {
      this.loading = false;
    }
  }

  /** Mean of a case's four judge dimensions, or null when unjudged. */
  private caseJudgeAvg(c: EvalCaseResult): number | null {
    if (!c.judge) return null;
    return (c.judge.fit + c.judge.design + c.judge.flow + c.judge.grounding) / 4;
  }

  render(): TemplateResult {
    return html`
      ${this.styles()}
      <section class="page">
        <div class="page-head">
          <div class="titles">
            <h1>Evaluation decks</h1>
            <p class="page-sub">Every generated deck in a run, rendered for visual evaluation against its brief.</p>
          </div>
        </div>
        ${this.renderBody()}
      </section>
    `;
  }

  private renderBody(): TemplateResult {
    if (this.error) return html`<div class="state error">Couldn't load — ${this.error}</div>`;
    if (this.loading) return html`<div class="state">Loading run…</div>`;
    if (!this.run)
      return html`<div class="state">
        <p>No evaluation runs yet.</p>
        <p class="page-sub">Run <code>go run ./scripts/eval --suite golden --judge</code> to create one.</p>
      </div>`;
    const cases = this.run.data?.cases ?? [];
    return html`
      ${this.renderHeader(this.run)}
      ${cases.length === 0
        ? html`<div class="state">This run stored no per-case detail.</div>`
        : html`<div class="edk-grid">${cases.map((c) => this.renderCase(c))}</div>`}
    `;
  }

  private renderHeader(run: EvalRun): TemplateResult {
    const detRate = run.detPassRate;
    const minScale = run.avgMinScale;
    const judge = run.judgeOverall;
    return html`
      <div
        class="edk-header"
        data-run-id=${run.id}
        data-det-pass-rate=${detRate.toFixed(4)}
        data-avg-min-scale=${minScale.toFixed(4)}
      >
        <div class="edk-hk"><b>${run.id}</b><span>run</span></div>
        <div class="edk-hk"><b>${run.suite}</b><span>suite</span></div>
        <div class="edk-hk"><b>${Math.round(detRate * 100)}%</b><span>det pass-rate</span></div>
        <div class="edk-hk"><b>${minScale.toFixed(2)}</b><span>avg min-scale</span></div>
        ${run.judged
          ? html`<div class="edk-hk"><b>${judge != null ? judge.toFixed(2) : '—'}</b><span>judge (overall)</span></div>`
          : ''}
        <div class="edk-hmeta">${run.data?.cases.length ?? run.cases} cases · ${run.timestamp}</div>
      </div>
    `;
  }

  private renderCase(c: EvalCaseResult): TemplateResult {
    const minScale = c.renderFit ? c.renderFit.minScale : null;
    const overflow = c.renderFit ? c.renderFit.overflowSlides : 0;
    const judgeAvg = this.caseJudgeAvg(c);
    const renderable = Boolean(c.model) && !c.error;

    let srcdoc = '';
    let renderErr: string | null = null;
    if (renderable) {
      try {
        // The eval `model` is typed DeckModel but at runtime is a V2 SlideDeck
        // (schemaVersion 2); render it with the V2 renderer.
        srcdoc = renderSlidesDoc(c.model as unknown as SlideDeck);
      } catch (err) {
        renderErr = err instanceof Error ? err.message : 'render failed';
      }
    }

    return html`
      <article
        class="edk-card"
        data-case-id=${c.id}
        data-det-pass=${c.deterministicPass ? 'true' : 'false'}
        data-min-scale=${minScale != null ? minScale.toFixed(4) : ''}
        data-overflow=${String(overflow)}
      >
        <div class="edk-card-head">
          <div class="edk-card-title">
            <span class="edk-case-id">${c.id}</span>
            <span class=${c.deterministicPass ? 'edk-pill ok' : 'edk-pill bad'}>${c.deterministicPass ? 'det pass' : 'det FAIL'}</span>
            ${judgeAvg != null ? html`<span class="edk-pill">judge ${judgeAvg.toFixed(1)}/5</span>` : ''}
          </div>
        </div>

        <div class="edk-frame">
          ${renderable && !renderErr
            ? html`<iframe .srcdoc=${srcdoc} title=${`deck ${c.id}`} loading="lazy"></iframe>`
            : html`<div class="edk-noframe">${c.error ? `Generation error: ${c.error}` : renderErr ? `Could not render: ${renderErr}` : 'No deck model stored for this case.'}</div>`}
        </div>

        <div class="edk-detail">
          ${this.renderBrief(c)}
          ${this.renderExpectations(c)}
          ${this.renderChecks(c.checks ?? [])}
          ${this.renderFit(c)}
          ${this.renderJudge(c)}
        </div>
      </article>
    `;
  }

  private renderBrief(c: EvalCaseResult): TemplateResult {
    if (!c.brief) return html``;
    return html`
      <div class="edk-sec">
        <div class="edk-sec-h">Brief</div>
        <p class="edk-brief">${c.brief}</p>
      </div>
    `;
  }

  /** The expected rubric carried by the must:* / should:* checks. The run payload
   *  does not store the raw case expectations separately, so the must/should
   *  check names are the canonical expression of what the deck should satisfy. */
  private renderExpectations(c: EvalCaseResult): TemplateResult {
    const checks = c.checks ?? [];
    const must = checks.filter((ch) => ch.severity === 'must');
    const should = checks.filter((ch) => ch.severity === 'should');
    if (must.length === 0 && should.length === 0) return html``;
    return html`
      <div class="edk-sec">
        <div class="edk-sec-h">Expected</div>
        ${must.length
          ? html`<div class="edk-exp"><span class="edk-exp-lab">must</span>${must.map((ch) => html`<span class="edk-exp-item">${ch.name}</span>`)}</div>`
          : ''}
        ${should.length
          ? html`<div class="edk-exp"><span class="edk-exp-lab should">should</span>${should.map((ch) => html`<span class="edk-exp-item">${ch.name}</span>`)}</div>`
          : ''}
      </div>
    `;
  }

  private renderChecks(checks: EvalCheck[]): TemplateResult {
    if (checks.length === 0) return html`<div class="edk-sec"><div class="edk-sec-h">Checks</div><p class="faint">No checks recorded.</p></div>`;
    return html`
      <div class="edk-sec">
        <div class="edk-sec-h">Checks</div>
        <ul class="edk-checks">
          ${checks.map(
            (ch) => html`<li
              class="edk-check"
              data-check-name=${ch.name}
              data-check-pass=${ch.pass ? 'true' : 'false'}
            >
              <span class=${ch.pass ? 'edk-mark ok' : 'edk-mark bad'}>${ch.pass ? '✓' : '✗'}</span>
              <span class="edk-check-name">${ch.name}</span>
              <span class="edk-check-sev ${ch.severity}">${ch.severity}</span>
              ${ch.detail ? html`<span class="edk-check-detail faint">${ch.detail}</span>` : ''}
            </li>`,
          )}
        </ul>
      </div>
    `;
  }

  private renderFit(c: EvalCaseResult): TemplateResult {
    const f = c.renderFit;
    if (!f) return html``;
    const scaleBad = f.minScale < 0.75;
    return html`
      <div class="edk-sec">
        <div class="edk-sec-h">Render-fit</div>
        <div class="edk-fit">
          <span class=${scaleBad ? 'edk-fit-k bad' : 'edk-fit-k'}>min-scale ${f.minScale.toFixed(2)}</span>
          <span class="edk-fit-k">below threshold ${f.belowThreshold}</span>
          <span class=${f.overflowSlides > 0 ? 'edk-fit-k bad' : 'edk-fit-k'}>overflow ${f.overflowSlides}</span>
        </div>
      </div>
    `;
  }

  private renderJudge(c: EvalCaseResult): TemplateResult {
    const j = c.judge;
    if (!j) return html``;
    return html`
      <div class="edk-sec">
        <div class="edk-sec-h">Judge</div>
        <div class="edk-judge">
          <span class="edk-judge-k">fit ${j.fit}</span>
          <span class="edk-judge-k">design ${j.design}</span>
          <span class="edk-judge-k">flow ${j.flow}</span>
          <span class="edk-judge-k">grounding ${j.grounding}</span>
        </div>
        ${j.rationale ? html`<p class="edk-rat">“${j.rationale}”</p>` : ''}
      </div>
    `;
  }

  private styles(): TemplateResult {
    return html`<style>
      .edk-header { display: flex; gap: 18px; flex-wrap: wrap; align-items: center; margin-bottom: 18px; }
      .edk-hk { background: var(--surface, #fff); padding: 10px 14px; border-radius: 8px; font-size: 12px; color: var(--text-tertiary, #888); }
      .edk-hk b { display: block; font-size: 20px; color: var(--text-primary, #1a1a1a); }
      .edk-hmeta { font-size: 11px; color: var(--text-tertiary, #888); margin-left: auto; }
      .edk-grid { display: grid; grid-template-columns: 1fr; gap: 24px; }
      @media (min-width: 1200px) { .edk-grid { grid-template-columns: 1fr 1fr; } }
      .edk-card { background: var(--surface, #fff); border: 1px solid var(--border-default, #ddd); border-radius: 12px; overflow: hidden; display: flex; flex-direction: column; }
      .edk-card-head { padding: 12px 14px; border-bottom: 1px solid var(--border-subtle, #eee); }
      .edk-card-title { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
      .edk-case-id { font-weight: 600; font-size: 14px; color: var(--text-primary, #1a1a1a); }
      .edk-pill { font-size: 11px; padding: 2px 8px; border-radius: 999px; background: var(--surface-hover, #eee); color: var(--text-secondary, #555); }
      .edk-pill.ok { background: #e2efe7; color: #3f7a55; } .edk-pill.bad { background: #f2e1de; color: #a03020; }
      /* Fixed-width stage so the 1920x1080 iframe scales deterministically
         (600/1920 = 0.3125) and the deck fills the frame edge-to-edge. */
      .edk-frame { width: 600px; max-width: 100%; aspect-ratio: 16 / 9; background: #0b0d10; position: relative; overflow: hidden; margin: 0 auto; }
      .edk-frame iframe { position: absolute; top: 0; left: 0; width: 1920px; height: 1080px; border: 0; transform: scale(0.3125); transform-origin: top left; }
      .edk-noframe { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; text-align: center; padding: 20px; color: #c8cdd4; font-size: 13px; }
      .edk-detail { padding: 14px; display: flex; flex-direction: column; gap: 14px; }
      .edk-sec-h { font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; color: var(--text-tertiary, #888); margin-bottom: 6px; }
      .edk-brief { margin: 0; font-size: 13px; color: var(--text-secondary, #555); line-height: 1.45; }
      .edk-exp { display: flex; flex-wrap: wrap; gap: 4px; align-items: center; margin-bottom: 4px; }
      .edk-exp-lab { font-size: 10px; text-transform: uppercase; letter-spacing: 0.04em; color: #a03020; font-weight: 600; margin-right: 4px; }
      .edk-exp-lab.should { color: var(--text-tertiary, #888); }
      .edk-exp-item { font-size: 11px; padding: 1px 7px; border-radius: 4px; background: var(--surface-hover, #eee); color: var(--text-secondary, #555); }
      .edk-checks { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 4px; }
      .edk-check { display: flex; align-items: baseline; gap: 6px; flex-wrap: wrap; font-size: 12px; }
      .edk-mark { font-weight: 700; } .edk-mark.ok { color: #3f7a55; } .edk-mark.bad { color: #a03020; }
      .edk-check-name { color: var(--text-primary, #1a1a1a); }
      .edk-check-sev { font-size: 10px; text-transform: uppercase; letter-spacing: 0.03em; color: var(--text-tertiary, #888); }
      .edk-check-sev.must { color: #a03020; }
      .edk-check-detail { font-size: 11px; }
      .edk-fit, .edk-judge { display: flex; flex-wrap: wrap; gap: 6px; }
      .edk-fit-k, .edk-judge-k { font-size: 11px; padding: 2px 8px; border-radius: 4px; background: var(--surface-hover, #eee); color: var(--text-secondary, #555); }
      .edk-fit-k.bad { background: #f2e1de; color: #a03020; }
      .edk-rat { margin: 6px 0 0; font-size: 12px; font-style: italic; color: var(--text-tertiary, #888); }
      .faint { color: var(--text-tertiary, #888); }
    </style>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'eval-decks-page': EvalDecksPage;
  }
}
